import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import en from "./locales/en.json";
import ru from "./locales/ru.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import it from "./locales/it.json";
import pt from "./locales/pt.json";
import pl from "./locales/pl.json";
import tr from "./locales/tr.json";
import ar from "./locales/ar.json";
import fa from "./locales/fa.json";
import hi from "./locales/hi.json";
import ja from "./locales/ja.json";
import ko from "./locales/ko.json";
import zh from "./locales/zh.json";
import az from "./locales/az.json";
import ro from "./locales/ro.json";
import bn from "./locales/bn.json";
import ta from "./locales/ta.json";
import ur from "./locales/ur.json";
import vi from "./locales/vi.json";
import { RTL_LANGS, DEFAULT_LANG } from "./languages";

const RAW_TRANSLATIONS = { en, ru, es, fr, de, it, pt, pl, tr, ar, fa, hi, ja, ko, zh, az, ro, bn, ta, ur, vi };
const STORAGE_KEY = "ns_lang";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Deep-merges each locale onto English so any missing/incomplete key
// falls back to English instead of crashing or rendering blank.
function deepMerge(base, override) {
  if (Array.isArray(base) || Array.isArray(override)) return override ?? base;
  if (typeof base !== "object" || base === null) return override ?? base;
  const out = { ...base };
  for (const key of Object.keys(override || {})) {
    out[key] = deepMerge(base[key], override[key]);
  }
  return out;
}

const TRANSLATIONS = Object.fromEntries(
  Object.entries(RAW_TRANSLATIONS).map(([code, dict]) => [code, code === "en" ? dict : deepMerge(en, dict)])
);

const SUPPORTED_LANGS = new Set(Object.keys(RAW_TRANSLATIONS));
const PRODUCTION_URL = "https://www.neuroscanapp.io";

// Reads navigator.languages (in browser preference order) and returns the
// first one that matches a supported locale, e.g. "pt-BR" -> "pt".
function detectBrowserLanguage() {
  const candidates = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (const candidate of candidates || []) {
    const base = candidate?.split("-")[0]?.toLowerCase();
    if (base && SUPPORTED_LANGS.has(base)) return base;
  }
  return null;
}

function getUrlLanguage() {
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  return urlLang && SUPPORTED_LANGS.has(urlLang) ? urlLang : null;
}

function syncUrlLanguage(code) {
  const url = new URL(window.location.href);
  if (url.searchParams.get("lang") === code) return;
  url.searchParams.set("lang", code);
  window.history.replaceState({}, "", url);
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const urlLang = getUrlLanguage();
  const storedLang = localStorage.getItem(STORAGE_KEY);
  const [language, setLanguageState] = useState(() => urlLang || storedLang || null);
  // If we already know the language (URL param or stored choice), skip the loading screen.
  const [ready, setReady] = useState(() => !!(urlLang || storedLang));

  useEffect(() => {
    if (language) {
      setReady(true);
      return;
    }

    const browserLang = detectBrowserLanguage();
    if (browserLang) {
      setLanguageState(browserLang);
      setReady(true);
      return;
    }

    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/detect-language`)
      .then((res) => {
        if (!cancelled) {
          setLanguageState(res.data?.language || DEFAULT_LANG);
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLanguageState(DEFAULT_LANG);
          setReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  useEffect(() => {
    if (!language) return;
    document.documentElement.lang = language;
    document.documentElement.dir = RTL_LANGS.includes(language) ? "rtl" : "ltr";
  }, [language]);

  const setLanguage = (code) => {
    localStorage.setItem(STORAGE_KEY, code);
    setLanguageState(code);
    syncUrlLanguage(code);
  };

  const activeLang = language || DEFAULT_LANG;
  const t = useMemo(() => TRANSLATIONS[activeLang] || TRANSLATIONS[DEFAULT_LANG], [activeLang]);

  useEffect(() => {
    const seo = t?.seo;
    if (!seo) return;
    syncUrlLanguage(activeLang);
    const canonicalUrl = `${PRODUCTION_URL}/?lang=${activeLang}`;
    document.title = seo.title;
    const setMeta = (selector, attr, content) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, content);
    };
    setMeta('meta[name="description"]', "content", seo.description);
    setMeta('meta[property="og:title"]', "content", seo.title);
    setMeta('meta[property="og:description"]', "content", seo.description);
    setMeta('meta[property="og:locale"]', "content", activeLang);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[name="twitter:title"]', "content", seo.title);
    setMeta('meta[name="twitter:description"]', "content", seo.description);
    setMeta('link[rel="canonical"]', "href", canonicalUrl);
    const ldJson = document.getElementById("ld-json-app");
    if (ldJson) {
      ldJson.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "NeuroScan",
        applicationCategory: "LifestyleApplication",
        operatingSystem: "ANDROID",
        description: seo.description,
        url: canonicalUrl,
        image: `${PRODUCTION_URL}/assets/og-image.jpg`,
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: "9.99",
          highPrice: "89.99",
          offerCount: "3",
        },
      });
    }
    const ldFaq = document.getElementById("ld-json-faq");
    if (ldFaq && t?.faq?.items?.length) {
      ldFaq.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: t.faq.items.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      });
    }
    const ldBreadcrumb = document.getElementById("ld-json-breadcrumb");
    if (ldBreadcrumb && t?.nav) {
      ldBreadcrumb.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "NeuroScan", item: canonicalUrl },
          { "@type": "ListItem", position: 2, name: t.nav.reports, item: `${canonicalUrl}#reports` },
          { "@type": "ListItem", position: 3, name: t.nav.pricing, item: `${canonicalUrl}#pricing` },
          { "@type": "ListItem", position: 4, name: t.nav.faq, item: `${canonicalUrl}#faq` },
        ],
      });
    }
  }, [activeLang, t]);

  if (!ready) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#050505]" data-testid="lang-loading-screen">
        <img src="/assets/logo.png" alt="NeuroScan" className="w-14 h-14 logo-neon" />
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={{ language: activeLang, setLanguage, t }}>
      <div className="lang-content-fade-in">{children}</div>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
